import styled from "@emotion/styled";
import { useState } from "react";
import { useTheme } from "emotion-theming";
import { aggregations } from "../../../../../utils/constants/constants";
import IOButton from "../../../../../UI/Form/Button/IOButton";
import { scrollbarDe } from "../../../../../styles/styledSnippets";
import { getRestOverrides } from "../../../utils/tableValidation";
import ColumnMonster from "./ColumnMonster";
import Settings from "./Settings";
import Tabs from "./Tabs";
import Styles from "./Styles/Styles";
import Flex from "../../../../../UI/Flex/Flex";
import { getRidOfAggregation } from "../../../../../charts/TableView/Elements/EditableMenu";

const Container = styled.div(
  ({ theme }) => `
  padding: 10px;
  text-align: left;
  height: 97%;
  overflow-y: auto;
  ${scrollbarDe(theme)};
`
);

export default function ColumnSettings({
  setChartState,
  selectedColumn,
  overrides,
  setSelectedColumn,
  coloredColumns,
  dynamicShadeBackgrounds,
  dynamicShadeBackgroundsBaseZero,
  generalColumnsWidth,
  dynamicColumnsWidth,
  maxColumnsWidth,
  renderAsImageKeys,
  removeColumn,
  removeDynamicSubTitleColumn,
  query,
  navigationState,
  rawRows,
  subTitles,
  fields,
  staticGroupingKeys,
  dynamicSubTitleKeys,
  chart,
  previewChart,
  immutableChart,
}) {
  const theme = useTheme();
  const [onStyles, setOnStyles] = useState();

  if (!selectedColumn) {
    return (
      <Container>
        <ColumnMonster />
      </Container>
    );
  }

  const { column, subIndex, index, name, aggregation, ops } = selectedColumn;
  const formulaType = column.includes("fn::");
  const openMathFormulaType = column.includes("omf::");

  function refreshOverrides(rest) {
    const filtered = overrides.filter((o) => rest.includes(o.name));
    return [...filtered, fields.find((f) => f.name === name)];
  }

  const updateOverrides = (newSubTitles) => {
    const rest = getRestOverrides(newSubTitles);
    const newOverrides = formulaType
      ? refreshOverrides(rest)
      : overrides.filter((o) => {
          if (o.name === name) {
            return rest.includes(name);
          }
          return true;
        });

    setChartState({ ...chart, overrides: newOverrides });
  };

  function getPrefix() {
    return (
      aggregations.find((aggr) => aggr.value === aggregation)?.prefix || ""
    );
  }

  function updateColumn(column) {
    setSelectedColumn({ ...selectedColumn, subIndex, column });
  }

  function updateOverride() {
    return chart.overrides.map((override) =>
      override.name === name ? { name } : override
    );
  }

  function resetColumnOverride() {
    setChartState({
      ...chart,
      overrides: updateOverride(),
      subTitles: chart.subTitles.map((subTitle, i) => {
        return subTitle.map((sub, j) =>
          i === subIndex && j === index && column === sub
            ? getRidOfAggregation(sub)
            : sub
        );
      }),
    });

    setSelectedColumn({
      column: getRidOfAggregation(column),
      name,
      subIndex,
      index,
    });
  }

  const switchColumnType = () => {
    const prefix = getPrefix();

    const newSub = formulaType
      ? prefix + (ops?.alias || name || "...")
      : `fn::NONE::...`;
    if (!dynamicSubTitleKeys) {
      const newSubTitles = subTitles.map((subTitle, i) =>
        subTitle.map((sub, j) =>
          (sub === column || sub === ops?.alias) &&
          i === subIndex &&
          j === index
            ? newSub
            : sub
        )
      );
      updateOverrides(newSubTitles);

      setChartState({
        ...chart,
        subTitles: newSubTitles,
      });
    } else {
      setChartState({
        ...chart,
        dynamicSubTitleKeys: chart.dynamicSubTitleKeys.map((key) =>
          key === column ? newSub : key
        ),
      });
    }

    updateColumn(newSub);
  };

  const switchToOpenMathColumnType = () => {
    const newSub = `omf::NONE::...`;
    setSelectedColumn({ index, subIndex, column: newSub, name: "" });
    setChartState({
      ...chart,
      subTitles: subTitles.map((subTitle, i) =>
        subTitle.map((sub, j) => (i === subIndex && j === index ? newSub : sub))
      ),
    });
  };

  const columnProps = {
    chart,
    overrides,
    selectedColumn,
    setChartState,
    setSelectedColumn,
    theme,
    query,
    navigationState,
    fields,
    staticGroupingKeys,
    subTitles,
    dynamicSubTitleKeys,
    formulaType,
    openMathFormulaType,
    switchColumnType,
    switchToOpenMathColumnType,
    rawRows,
    coloredColumns,
    dynamicShadeBackgrounds,
    dynamicShadeBackgroundsBaseZero,
    generalColumnsWidth,
    dynamicColumnsWidth,
    maxColumnsWidth,
    renderAsImageKeys,
    previewChart,
    immutableChart,
  };

  const onRemove = () => {
    if (chart.dynamicSubTitleKeys) {
      return removeDynamicSubTitleColumn(column);
    }
    removeColumn(chart, subIndex, index);
  };

  return (
    <Container>
      <Tabs onStyles={onStyles} setOnStyles={setOnStyles} />
      {onStyles ? <Styles {...columnProps} /> : <Settings {...columnProps} />}
      <br />
      <Flex justifyContent="space-around">
        <IOButton
          onClick={resetColumnOverride}
          style={{ padding: "8px 12px" }}
          title="Reset Override will drop mappings and aggregation for current column"
          cy="reset-column-override"
        >
          Reset Override
        </IOButton>
        <IOButton
          error
          onClick={onRemove}
          style={{ padding: "8px 12px" }}
          cy="remove-column"
        >
          Remove Column
        </IOButton>
      </Flex>
    </Container>
  );
}
